<template>
  <div class="solution-page">
    <!-- 导航区域 -->
    <ProductNavigation />

    <!-- 解决方案页面头部section -->
    <section class="solution-hero-section">
     
    </section>

    <!-- 解决方案列表section -->
    <section class="solution-list-section">
      <!-- 筛选栏容器 -->
      <div class="filter-container">
        <div class="filter-item">
          <label class="filter-label">{{ t('solutions.solutionType') }}:</label>
          <div class="type-selector">
            <button 
              v-for="type in solutionTypes" 
              :key="type"
              @click="selectType(type)"
              :class="{ 'active': selectedType === type }"
            >
              {{ getTypeLabel(type) }}
            </button>
          </div>
        </div>
      </div>
      <!-- 动态解决方案展示网格 -->
      <div class="solution-grid">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-spinner">{{ t('common.loading') }}</div>
          <!-- 解决方案项 -->
          <router-link 
            :to="{ name: 'SolutionDetail', params: { id: solution.id } }"
            class="solution-link"
            v-for="solution in filteredSolutions" 
            :key="solution.id"
          >
            <div class="solution-item" :class="getGradientClass()">
              <div class="solution-header">
                 <div class="solution-category">{{ 
                   getCategoryLabel(
                     locale.value === 'en' 
                       ? (solution.categoryEn || solution.category) 
                       : solution.category
                   ) || t('solutions.solution')
                 }}</div>
               </div>
              <div class="solution-content">
                <h3 class="solution-title">{{ 
                  locale.value === 'en' 
                    ? (solution.titleEn || solution.title) 
                    : solution.title 
                }}</h3>
                <p class="solution-description">{{ 
                  locale.value === 'en'
                    ? (solution.briefDescriptionEn || solution.briefDescription || solution.descriptionEn || solution.description?.substring(0, 80) + '...' || t('solutions.defaultDescription'))
                    : (solution.briefDescription || solution.description?.substring(0, 80) + '...' || t('solutions.defaultDescription'))
                }}</p>
              </div>
              <div class="solution-footer">
                <div class="solution-features">
                  <span class="feature-tag">{{ t('solutions.professionalCustom') }}</span>
                  <span class="feature-tag">{{ t('solutions.efficientReliable') }}</span>
                </div>
                <div class="solution-arrow">→</div>
              </div>
            </div>
          </router-link>
      </div>

        <!-- 无数据状态 -->
      <div v-if="!loading && filteredSolutions.length === 0" class="no-data">
          {{ t('solutions.noSolutions') }}
      </div>

            <!-- 分页控件 -->
      <div v-if="!loading && totalPages >= 1" class="pagination">
        <button class="page-btn" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">{{ t('pagination.previous') }}</button>
        <button 
          class="page-btn"
          v-for="page in totalPages" 
          :key="page"
          @click="changePage(page)"
          :class="{ 'active': currentPage === page }"
        >
          {{ page }}
        </button>
        <button class="page-btn" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">{{ t('pagination.next') }}</button>
      </div>

    </section>

    <!-- 页脚内容 -->
    <Footer />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getAllSolutions } from '@/api/solution';
import ProductNavigation from '@/components/ProductNavigation.vue';
import Footer from '@/components/Footer.vue';
import { solutionCategories } from '@/config/solutionTemplate.js';

const { t, locale } = useI18n();
// 初始化响应式变量
const solutions = ref([]);

// 获取类别对应的标签（根据当前语言）
const getCategoryLabel = (categoryValue) => {
  const category = solutionCategories.find(cat => cat.value === categoryValue);
  if (!category) {
    return categoryValue;
  }
  // 根据当前语言返回对应标签
  if (locale.value === 'en') {
    return category.labelEn || category.label || categoryValue;
  }
  return category.label || categoryValue;
};

// 获取标签对应的值（支持中英文标签）
const getCategoryValue = (categoryLabel) => {
  // 先尝试通过中文标签查找
  let category = solutionCategories.find(cat => cat.label === categoryLabel);
  // 如果没找到，尝试通过英文标签查找
  if (!category) {
    category = solutionCategories.find(cat => cat.labelEn === categoryLabel);
  }
  // 如果还是没找到，尝试直接匹配 value
  if (!category) {
    category = solutionCategories.find(cat => cat.value === categoryLabel);
  }
  return category ? category.value : categoryLabel;
};
// 解决方案类型筛选相关变量
const solutionTypes = ref([t('solutions.allTypes')]); // 初始值
const selectedType = ref(t('solutions.allTypes'));
const currentPage = ref(1);
const pageSize = ref(6);
const loading = ref(false);

// 加载解决方案数据 - 修改为提取类型信息
const loadSolutions = async () => {
  try {
    loading.value = true;
    // 调用带分页参数和语言参数的API
    const response = await getAllSolutions(currentPage.value, pageSize.value, locale.value);
    // 确保solutions.value始终是数组
    solutions.value = Array.isArray(response.data?.data) ? response.data.data : [];
    // 更新总页数
    totalCount.value = response.data?.total || 0;
    
    // 提取所有分类并去重
    const categories = [t('solutions.allTypes')];
    const uniqueCategories = new Set();
    solutions.value.forEach(solution => {
      // 根据当前语言选择类别字段
      const categoryValue = locale.value === 'en' ? (solution.categoryEn || solution.category) : solution.category;
      if (categoryValue) {
        // 获取类别对应的标签（根据当前语言）
        const categoryLabel = getCategoryLabel(categoryValue);
        uniqueCategories.add(categoryLabel);
      }
    });
    solutionTypes.value = categories.concat(Array.from(uniqueCategories));
  } catch (error) {
    console.error('加载解决方案失败:', error);
    solutions.value = [];
    alert(t('solutions.loadFailed'));
  } finally {
    loading.value = false;
  }
};

// 添加总条数变量
const totalCount = ref(0);


// 筛选解决方案（仅筛选，不分页）
const allFilteredSolutions = computed(() => {
  let result = solutions.value || [];
  // 类型筛选 - 将标签转换为值进行比较
  if (selectedType.value && selectedType.value !== t('solutions.allTypes')) {
    const categoryValue = getCategoryValue(selectedType.value);
    result = result.filter(solution => {
      // 根据当前语言选择类别字段进行比较
      const solutionCategory = locale.value === 'en' ? (solution.categoryEn || solution.category) : solution.category;
      return solutionCategory === categoryValue;
    });
  }
  return result;
});

// 分页处理（基于已筛选的全部解决方案）
const filteredSolutions = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value;
  return allFilteredSolutions.value.slice(startIndex, startIndex + pageSize.value);
});

// 类型选择方法
const selectType = (type) => {
  selectedType.value = type;
  currentPage.value = 1; // 重置页码
};

// 更新总页数计算逻辑
const totalPages = computed(() => {
  // 基于所有筛选后的解决方案总数计算总页数
  return Math.ceil(allFilteredSolutions.value.length / pageSize.value) || 1;
});

// 切换页码
const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

// 获取类型标签（用于显示）
const getTypeLabel = (type) => {
  if (type === '所有类型' || type === t('solutions.allTypes')) {
    return t('solutions.allTypes');
  }
  return type;
};

// 统一使用蓝色主题渐变
const getGradientClass = () => {
  return 'gradient-blue'; // 统一使用蓝色主题
};

// 监听语言变化
watch(() => locale.value, () => {
  selectedType.value = t('solutions.allTypes');
  loadSolutions();
});

// 页面加载时获取数据
onMounted(() => {
  loadSolutions();
  document.title = locale.value === 'zh' ? '解决方案 - Fabet Company' : 'Solutions - Fabet Company';
});
</script>

<style scoped lang="scss">
/* 导航样式 */
@import '@/styles/nav_section';
/* 原有解决方案页面样式 */
@import '@/styles/sections/homepage/main_menu';
@import '@/styles/sections/product_page/product_head';
@import '@/styles/sections/product_page/product_list';
@import '@/styles/sections/homepage/footer_section';

.solution-page {
  padding: 0;
}

.solution-hero-section {
  position: relative;
  height: 90vh;
  background-color: #f5f5f5;
  background-image: url('@/assets/solution_banner.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.solution-list-section {
  padding: 60px 0;
  background-color: #f9f9f9;
}

.filter-container {
  max-width: 1200px;
  margin: 0 auto 30px;
  padding: 0 20px;
}

.filter-item {
  display: flex;
  align-items: center;
}

.filter-label {
  margin-right: 15px;
  font-weight: bold;
  color: #333;
}

.type-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.type-selector button {
  padding: 8px 15px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.type-selector button.active {
  background-color: #4285f4;
  color: #fff;
  border-color: #4285f4;
}

// 解决方案卡片网格样式
.solution-grid {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 30px;
}

.solution-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.solution-item {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  color: white;
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 280px;
}

.solution-item.gradient-blue {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
}

.solution-item.gradient-purple {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
  color: #333;
}

.solution-item.gradient-purple .solution-icon {
  background: rgba(0, 0, 0, 0.1);
}

.solution-item.gradient-purple .solution-icon .icon {
  color: #333;
}

.solution-item.gradient-purple .solution-category,
.solution-item.gradient-purple .feature-tag {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

.solution-item.gradient-green {
  background: linear-gradient(135deg, #d299c2 0%, #fef9d7 100%);
  color: #333;
}

.solution-item.gradient-green .solution-icon {
  background: rgba(0, 0, 0, 0.1);
}

.solution-item.gradient-green .solution-icon .icon {
  color: #333;
}

.solution-item.gradient-green .solution-category,
.solution-item.gradient-green .feature-tag {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

.solution-item.gradient-orange {
  background: linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%);
}

.solution-item.gradient-pink {
  background: linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%);
}

.solution-item.gradient-teal {
  background: linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%);
  color: #333;
}

.solution-item.gradient-teal .solution-icon {
  background: rgba(0, 0, 0, 0.1);
}

.solution-item.gradient-teal .solution-icon .icon {
  color: #333;
}

.solution-item.gradient-teal .solution-category,
.solution-item.gradient-teal .feature-tag {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}

.solution-link:hover .solution-item {
  transform: translateY(-8px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.solution-header {
   display: flex;
   align-items: center;
   justify-content: flex-end;
   margin-bottom: 16px;
 }

.solution-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.solution-icon .icon {
  font-size: 20px;
  color: white;
}

.solution-category {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.solution-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.solution-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.solution-description {
  font-size: 14px;
  line-height: 1.5;
  opacity: 0.9;
  margin: 0;
  flex: 1;
}

.solution-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
}

.solution-features {
  display: flex;
  gap: 8px;
}

.feature-tag {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.solution-arrow {
  font-size: 18px;
  font-weight: bold;
  opacity: 0.8;
  transition: transform 0.3s ease;
}

.solution-link:hover .solution-arrow {
  transform: translateX(4px);
}

.loading-spinner {
  text-align: center;
  padding: 50px 0;
  color: #666;
  grid-column: 1 / -1;
}

.no-data {
  text-align: center;
  padding: 50px 0;
  color: #666;
  grid-column: 1 / -1;
}

.pagination {
  max-width: 1200px;
  margin: 30px auto 0;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.page-btn {
  padding: 8px 15px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.page-btn.active {
  background-color: #4285f4;
  color: #fff;
  border-color: #4285f4;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 移动端响应式适配 */
@media (max-width: 768px) {
  .solution-hero-section {
    height: 60vh;
    padding: 40px 0;
    background-position: center center;
    background-size: cover;
  }
  
  .hero-content {
    padding: 0 15px;
  }
  
  .solution-list-section {
    padding: 40px 0;
  }
  
  .filter-container {
    padding: 0 15px;
  }
  
  .filter-item {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 15px;
  }
  
  .filter-label {
    margin-bottom: 10px;
    margin-right: 0;
  }
  
  .type-selector {
    width: 100%;
  }
  
  .type-selector button {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .solution-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 0 15px;
  }
  
  .solution-item img {
    height: 200px;
  }
  
  .solution-name {
    font-size: 16px;
    margin: 12px;
  }
  
  .pagination {
    padding: 0 15px;
    flex-wrap: wrap;
    gap: 5px;
  }
  
  .page-btn {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .loading-spinner {
    font-size: 14px;
  }
  
  .no-data {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .solution-hero-section {
    height: 50vh;
    min-height: 400px;
    padding: 30px 0;
    background-position: center center;
  }
  
  .hero-content {
    padding: 0 10px;
  }
  
  .solution-list-section {
    padding: 30px 0;
  }
  
  .filter-container {
    padding: 0 10px;
  }
  
  .type-selector button {
    padding: 5px 10px;
    font-size: 13px;
  }
  
  .solution-grid {
    gap: 15px;
    padding: 0 10px;
  }
  
  .solution-item img {
    height: 180px;
  }
  
  .solution-name {
    font-size: 15px;
    margin: 10px;
  }
  
  .pagination {
    padding: 0 10px;
  }
  
  .page-btn {
    padding: 5px 10px;
    font-size: 13px;
    min-width: 35px;
  }
  
  .loading-spinner,
  .no-data {
    padding: 30px 0;
    font-size: 14px;
  }
}

@media (max-width: 360px) {
  .solution-hero-section {
    height: 45vh;
    min-height: 350px;
  }
  
  .solution-item img {
    height: 160px;
  }
  
  .solution-name {
    font-size: 14px;
  }
}
</style>